import { css, theme } from 'twind/css'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef } from 'react'
import { useTw } from '../../../../../../utils/Styles'
import { PaperAirplaneIcon } from '@heroicons/react/outline'
import { AttachmentDrop } from './Attachments/AttachmentDrop'
import { AttachmentItem } from './Attachments/AttachmentItem'
import { Formik, Form, useField, useFormikContext } from 'formik'
import { AttachmentButton } from './Attachments/AttachmentButton'
import { AttachmentsProvider, useAttachments } from './Attachments'

type OnSend = (message: string, attachments: string[]) => Promise<unknown>

type FormType = {
  message: string
  attachments: string[] | 'reset'
}

const style = css`
  &:empty:not(:focus)::before {
    pointer-events: none;
    content: attr(placeholder);
    color: ${theme('colors.neutral.300')};
  }
`

function ComposeInput() {
  const tw = useTw()
  const ref = useRef<HTMLDivElement>()
  const [t] = useTranslation('common')
  const [{ onBlur, value }, , { setValue }] = useField('message')

  useEffect(() => {
    if (ref.current && value !== ref.current.innerText)
      ref.current.innerText = value
  }, [ref, value])

  return (
    <div
      ref={ref}
      onBlur={onBlur}
      contentEditable
      placeholder={t('conversation.conversation.compose_placeholder')}
      className={tw`${style} inline-block p-2 max-h-32 overflow-scroll bg-transparent w-full sm:text-sm border-none focus:outline-none focus:ring-0 sm:leading-5 overflow-x-hidden`}
      onInput={(e) => {
        setValue(e.currentTarget.innerText)
      }}
      onPaste={(e) => {
        e.preventDefault()
        const text = e.clipboardData.getData('text')
        document.execCommand('insertText', false, text)
      }}
    />
  )
}

function SendButton() {
  const tw = useTw()
  const { attachment } = useAttachments()
  const { values, isValid, isSubmitting } = useFormikContext<FormType>()
  const hasContent = values.message.trim().length || values.attachments?.length
  const isLoading = attachment && '_id' in attachment === false
  const canSubmit = isValid && !isSubmitting && !isLoading && hasContent

  return (
    <button
      type="submit"
      disabled={!canSubmit}
      className={tw`px-2 h-9 ${
        canSubmit
          ? 'text-foreground cursor-pointer'
          : 'text-gray-500 cursor-default'
      }`}
    >
      <PaperAirplaneIcon className={tw`w-6 h-6 transform rotate-90`} />
    </button>
  )
}

export function Compose({ onSend }: { onSend: OnSend }) {
  const tw = useTw()
  return (
    <Formik<FormType>
      initialValues={{ message: '', attachments: [] }}
      onSubmit={(variables, api) => {
        onSend(
          variables.message,
          Array.isArray(variables.attachments) ? variables.attachments : []
        ).then(() => {
          api.resetForm({
            values: {
              message: '',
              attachments: 'reset',
            },
          })

          api.setSubmitting(false)
        })
      }}
    >
      <Form className={tw`h-auto`}>
        <AttachmentsProvider name="attachments">
          <AttachmentDrop>
            <AttachmentItem />
            <div className={tw`flex items-end p-2 border-t border-gray-300`}>
              <ComposeInput />
              <AttachmentButton />
              <SendButton />
            </div>
          </AttachmentDrop>
        </AttachmentsProvider>
      </Form>
    </Formik>
  )
}
